import { ReactNode } from 'react';

import { RiFileLine, RiFileTextLine, RiNotionFill } from '@remixicon/react';

import {
  ButtonIcon,
  IconExternalLink,
  Link,
  Text,
} from '@spotify-internal/encore-web';
import { Tooltip, TooltipRoot } from '@spotify-internal/encore-web/next';
import { DateUtils, Spinner } from '@spotify/confidence-core-react';
import { useExternalDocumentRef } from '@spotify/confidence-external-documents-react';
import {
  AttachmentFragment,
  CollaborationV1ExternalDocumentSourceType,
  ExternalDocumentRefFragment,
} from '@spotify/confidence-graphql';
import {
  createArtifactExtension,
  createModifierClickHandler,
  isExternalDocument,
  isSafeUrl,
} from '@spotify/plugin-confidence-artifacts-react';
import { MarkdownContent } from '@spotify/plugin-confidence-chat-react';
import { UNTITLED_DOCUMENT_TITLE } from '@spotify/plugin-confidence-collaboration-react';
import {
  ArtifactIcon,
  ArtifactListItem,
  ArtifactMenu,
  getGradientStartColor,
  getResourceName,
} from '@spotify/plugin-confidence-projects-react';

import { projectsPlugin } from '../plugin';

import styles from './ExternalDocumentArtifactExtension.module.css';

const getSourceLabel = (
  type?: CollaborationV1ExternalDocumentSourceType,
): string => {
  switch (type) {
    case CollaborationV1ExternalDocumentSourceType.GoogleDocs:
      return 'Google Docs';
    case CollaborationV1ExternalDocumentSourceType.GoogleSlides:
      return 'Google Slides';
    case CollaborationV1ExternalDocumentSourceType.Notion:
      return 'Notion';
    default:
      return 'External document';
  }
};

const getSafeUrl = (url: string | null | undefined): string | undefined =>
  url && isSafeUrl(url) ? url : undefined;

const OpenInSourceButton = ({
  sourceUrl,
  sourceLabel,
}: {
  sourceUrl: string | undefined;
  sourceLabel: string;
}) => {
  if (!sourceUrl) {
    return null;
  }

  return (
    <TooltipRoot>
      <ButtonIcon
        variant="tertiary"
        onClick={() => window.open(sourceUrl, '_blank', 'noopener,noreferrer')}
        aria-label={`Open in ${sourceLabel}`}
       data-track-source-anchor="94c18349ec;open-in-source-button-icon;low">
        <IconExternalLink  data-track-source-anchor="9fef48a319;open-in-source-button-icon-external-link"/>
      </ButtonIcon>
      <Tooltip placement="bottom">{`Open in ${sourceLabel}`}</Tooltip>
    </TooltipRoot>
  );
};

const ExternalDocumentMarkdownPreview = ({
  externalDocument,
  isLoading,
}: {
  externalDocument: ExternalDocumentRefFragment | undefined;
  isLoading: boolean;
}) => {
  if (isLoading && !externalDocument) {
    return (
      <div className={styles.loading}>
        <Spinner size={24} aria-label="Loading document" />
      </div>
    );
  }

  if (externalDocument?.markdown) {
    return <MarkdownContent>{externalDocument.markdown}</MarkdownContent>;
  }

  const safeSourceUrl = getSafeUrl(externalDocument?.source?.uri);
  const sourceLabel = getSourceLabel(externalDocument?.source?.type);

  return (
    <div className={styles.emptyState}>
      <Text variant="bodySmall" semanticColor="textSubdued">
        No preview available.
      </Text>
      {safeSourceUrl && (
        <Link
          href={safeSourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          isStandalone
         data-track-source-anchor="fb64d6cf3a;external-document-markdown-preview-link">
          Open in {sourceLabel}
        </Link>
      )}
    </div>
  );
};

const ExternalDocumentContent = ({
  artifact,
}: {
  artifact: AttachmentFragment;
}) => {
  const resourceName = getResourceName(artifact.uri);
  const { externalDocument, isLoading } = useExternalDocumentRef({
    name: resourceName,
  });

  return (
    <ExternalDocumentMarkdownPreview
      externalDocument={externalDocument}
      isLoading={isLoading}
    />
  );
};

const ExternalDocumentHeaderActions = ({
  artifact,
}: {
  artifact: AttachmentFragment;
}) => {
  const resourceName = getResourceName(artifact.uri);
  const { externalDocument } = useExternalDocumentRef({ name: resourceName });
  const safeSourceUrl = getSafeUrl(externalDocument?.source?.uri);
  const sourceLabel = getSourceLabel(externalDocument?.source?.type);

  return (
    <OpenInSourceButton sourceUrl={safeSourceUrl} sourceLabel={sourceLabel}  data-track-source-anchor="e723a9a0fd;external-document-header-actions-open-in-source"/>
  );
};

const getSourceIcon = (
  type: CollaborationV1ExternalDocumentSourceType | undefined,
): ReactNode => {
  switch (type) {
    case CollaborationV1ExternalDocumentSourceType.GoogleDocs:
      return <RiFileTextLine size={16} />;
    case CollaborationV1ExternalDocumentSourceType.Notion:
      return <RiNotionFill size={16} />;
    default:
      return <RiFileLine size={16} />;
  }
};

const ExternalDocumentListItem = ({
  artifact,
  onSelect,
  onDelete,
  onRename,
}: {
  artifact: AttachmentFragment;
  onSelect: () => void;
  onDelete?: () => void;
  onRename?: () => void;
}) => {
  const resourceName = getResourceName(artifact.uri);
  const { externalDocument } = useExternalDocumentRef({ name: resourceName });
  const safeSourceUrl = getSafeUrl(externalDocument?.source?.uri);
  const sourceLabel = getSourceLabel(externalDocument?.source?.type);
  const sourceIcon = getSourceIcon(externalDocument?.source?.type);
  const displayName =
    artifact.displayName || externalDocument?.title || UNTITLED_DOCUMENT_TITLE;
  const dateLabel = DateUtils.toDateString(
    new Date(artifact.createTime),
    DateUtils.MONTH_DAY_FORMAT,
  );
  const handleOpenExternal = () => {
    if (safeSourceUrl) {
      window.open(safeSourceUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <ArtifactListItem.Root
      onClick={createModifierClickHandler(safeSourceUrl, onSelect)}
     data-track-source-anchor="dcb9c5ca14;external-document-list-item-root">
      <ArtifactListItem.Icon>
        <ArtifactIcon>{sourceIcon}</ArtifactIcon>
      </ArtifactListItem.Icon>
      <ArtifactListItem.Content
        primary={displayName}
        secondary={
          <>
            {sourceLabel} &middot; {dateLabel}
          </>
        }
      />
      <ArtifactListItem.Action>
        <ArtifactMenu.Root
          aria-label="Document actions"
          copyUrl={safeSourceUrl}
          displayName={displayName}
          onRename={onRename}
          onDelete={onDelete}
        >
          <ArtifactMenu.Item icon={<RiFileLine size={16} />} onClick={onSelect} data-track-source-anchor="0a0fae5389">
            Open document
          </ArtifactMenu.Item>
          {safeSourceUrl && (
            <ArtifactMenu.Item
              icon={<IconExternalLink size="small"  data-track-source-anchor="740be9dee2;external-document-list-item-icon-external-link"/>}
              onClick={handleOpenExternal}
             data-track-source-anchor="599a88694b;external-document-list-item">
              {`Open in ${sourceLabel}`}
            </ArtifactMenu.Item>
          )}
        </ArtifactMenu.Root>
      </ArtifactListItem.Action>
    </ArtifactListItem.Root>
  );
};

export const ExternalDocumentArtifactExtension = projectsPlugin.provide(
  createArtifactExtension({
    id: 'external-document',
    predicate: artifact => isExternalDocument(artifact.uri),
    icon: () => ({
      Icon: RiFileLine,
      color: getGradientStartColor('blue'),
    }),
    label: 'Document',
    category: 'Documents',
    render: artifact => <ExternalDocumentContent artifact={artifact} />,
    renderHeaderActions: artifact => (
      <ExternalDocumentHeaderActions artifact={artifact} />
    ),
    renderListItem: (artifact, { onSelect, onDelete, onRename }) => (
      <ExternalDocumentListItem
        artifact={artifact}
        onSelect={onSelect}
        onDelete={onDelete}
        onRename={onRename}
      />
    ),
  }),
);
